// useCaptionTranslation — translates arbitrary text (captions,
// devotionals, dynamic strings) to the user's selected language.
//
// Caching strategy (Feb 2026 — "rapid fast v2 batched"):
//   1. In-memory `cache` Map  — fastest, no IO
//   2. localStorage `LS_KEY`  — persists across reloads, sessions,
//      logouts. Once a user has visited each page in Spanish, EVERY
//      subsequent visit is instant: zero /translate round-trips.
//   3. Per-frame BATCH coalescing — when 80 rows on a page mount
//      and each call this hook, we no longer fire 80 separate
//      POSTs. We collect every string requested within a 60ms
//      window and send ONE /translate-text call with the whole
//      array. /translate-text serves the same cache as /translate
//      so we don't double-translate. This collapsed the Dictionary
//      page's "language switch" from ~25-60 seconds to ~1.5 seconds
//      on a cold cache.
//   4. In-flight `inflight` Map — collapses duplicate concurrent
//      requests for the same string across batches (the
//      devotional + verse trigger two hooks but share one fetch).
//
// Race protection: tokenRef ensures a slow earlier response from a
// previous text/lang pair can't overwrite a newer one mid-fetch.
import { useEffect, useState, useRef, useCallback } from "react";
import api from "../lib/api";
import { useAudio } from "../context/AudioContext";

const LS_KEY = "hw:i18n:cache:v2";
const MAX_CACHE_ENTRIES = 2000; // ~600 KB localStorage budget

// One-time purge of the legacy v1 cache. The v1 cache filled up with
// stale MyMemory noops (e.g. "Soul Food" → "Soul Food" in Spanish)
// that locked English strings to English forever. Switching keys
// alone is enough — the old key just sits dead in localStorage. We
// also actively remove it to free the storage budget.
try {
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.removeItem("hw:i18n:cache:v1");
  }
} catch (_) {}

// ── localStorage bootstrap (runs once at import) ─────────────────
const cache = new Map();
try {
  if (typeof window !== "undefined" && window.localStorage) {
    const raw = window.localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        for (const [k, v] of Object.entries(parsed)) {
          if (typeof v === "string") cache.set(k, v);
        }
      }
    }
  }
} catch (_) { /* swallow — corrupt cache just means we re-translate */ }

// Per-key promise that resolves with the translated string. Lets
// multiple useCaptionTranslation calls with the same (lang, text)
// share a single network round-trip even across separate batches.
const inflight = new Map();

// ── Batched scheduler ───────────────────────────────────────────
// One queue per target language. We hold incoming requests for
// BATCH_DELAY_MS, then ship them as a single POST. New requests
// for the same lang during that window join the same batch. After
// the window closes we open a fresh queue. MAX_BATCH_SIZE caps the
// individual POST so a 4,000-row dictionary still chunks neatly.
//
// 15ms debounce is short enough to feel instant (under one display
// refresh on a 60Hz screen) and long enough that React batching
// during a route change still groups every <Tr> on the new page
// into one POST. We previously used 60ms which added a noticeable
// "stall" between tapping a language and seeing text change.
const BATCH_DELAY_MS = 15;
const MAX_BATCH_SIZE = 120;

const _pending = new Map(); // lang -> { texts:Set, resolvers:Map<text, resolve[]>, timer }

function _enqueue(lang, text) {
  // Return existing inflight promise if any.
  const key = `${lang}::${text}`;
  if (inflight.has(key)) return inflight.get(key);

  let entry = _pending.get(lang);
  if (!entry) {
    entry = { texts: new Set(), resolvers: new Map(), timer: null };
    _pending.set(lang, entry);
  }
  entry.texts.add(text);
  const p = new Promise((resolve) => {
    const arr = entry.resolvers.get(text) || [];
    arr.push(resolve);
    entry.resolvers.set(text, arr);
  });
  inflight.set(key, p);

  // Fire immediately if we hit the cap; otherwise debounce.
  if (entry.texts.size >= MAX_BATCH_SIZE) {
    if (entry.timer) { clearTimeout(entry.timer); entry.timer = null; }
    _flush(lang);
  } else if (!entry.timer) {
    entry.timer = setTimeout(() => _flush(lang), BATCH_DELAY_MS);
  }
  return p;
}

async function _flush(lang) {
  const entry = _pending.get(lang);
  if (!entry) return;
  _pending.delete(lang);
  if (entry.timer) { clearTimeout(entry.timer); }

  const texts = Array.from(entry.texts);
  let translations = [];
  try {
    const { data } = await api.post("/translate-text", {
      texts,
      lang,
      source: "en",
    });
    translations = (data && data.translations) || [];
  } catch (_) {
    translations = [];
  }

  for (let i = 0; i < texts.length; i++) {
    const src = texts[i];
    const tx = translations[i] || src;
    const key = `${lang}::${src}`;
    cache.set(key, tx);
    inflight.delete(key);
    const arr = entry.resolvers.get(src) || [];
    for (const r of arr) r(tx);
  }
  scheduleFlush();
}

// Flush in-memory cache back to localStorage. Coalesced via a
// 750ms idle timer so a burst of translations during a page load
// only writes once.
let _flushTimer = null;
function scheduleFlush() {
  if (typeof window === "undefined" || !window.localStorage) return;
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    try {
      let entries = Array.from(cache.entries());
      if (entries.length > MAX_CACHE_ENTRIES) {
        entries = entries.slice(entries.length - MAX_CACHE_ENTRIES);
        cache.clear();
        for (const [k, v] of entries) cache.set(k, v);
      }
      const out = {};
      for (const [k, v] of entries) out[k] = v;
      window.localStorage.setItem(LS_KEY, JSON.stringify(out));
    } catch (_) {
      try {
        const half = Math.floor(cache.size / 2);
        let i = 0;
        for (const k of cache.keys()) {
          if (i++ >= half) break;
          cache.delete(k);
        }
        const out = {};
        for (const [k, v] of cache.entries()) out[k] = v;
        window.localStorage.setItem(LS_KEY, JSON.stringify(out));
      } catch (__) {}
    }
  }, 750);
}

// Initial render value — checked synchronously from the in-memory
// cache so a hot path (which is now the COMMON path thanks to the
// bundled-translations boot hydrator) renders the translated string
// on the very first paint. No useState→useEffect→setState round-trip,
// no English flash. Returns the source if there's nothing cached.
function _initialValue(text, lang) {
  if (!text) return "";
  if (!lang || lang === "en") return text;
  const key = `${lang}::${text}`;
  return cache.has(key) ? cache.get(key) : text;
}

/**
 * Synchronous translator for imperative call-sites that can't render
 * a <Tr> component — toasts, alert/confirm, placeholder/aria-label/
 * title attributes, etc.
 *
 *   const tr = useTr();
 *   toast.success(tr("Surprise!"));
 *
 * Returns the cached translation if present; otherwise returns the
 * original English AND fires off a background fetch so the next call
 * (or a re-render with the same string) gets the localized form.
 * The hook subscribes to the active language so the returned function
 * is stable per (language, cache-revision).
 */
export function useTr() {
  const lang = useActiveLang(); // see helper below
  return useCallback((text) => {
    if (!text) return text;
    if (!lang || lang === "en") return text;
    const key = `${lang}::${text}`;
    if (cache.has(key)) return cache.get(key);
    // Kick off async fetch so future calls return the translation.
    // We deliberately don't await — toasts must fire immediately.
    _enqueue(lang, text).catch(() => { /* silent — English is fine */ });
    return text;
  }, [lang]);
}

// Internal: read the active language from the AudioContext without a
// hard dep on its export shape (the context exposes `language`).
// Falls back to "en" if not yet hydrated.
function useActiveLang() {
  const ctx = useAudio();
  return ctx?.language || "en";
}

export default function useCaptionTranslation(text, lang) {
  // useState lazy initializer — runs once per mount, reads from cache.
  // When the bundled-translations manifest has hydrated this entry,
  // the user sees the target language on the first render frame.
  const [translated, setTranslated] = useState(() => _initialValue(text, lang));
  const tokenRef = useRef(0);

  useEffect(() => {
    if (!text) { setTranslated(""); return; }
    if (!lang || lang === "en") { setTranslated(text); return; }
    const key = `${lang}::${text}`;
    if (cache.has(key)) {
      setTranslated(cache.get(key));
      return;
    }

    const myToken = ++tokenRef.current;
    // No cache hit AND we need a translation — show English while the
    // batch fetch lands. (The initial value above already did this if
    // it ran during the same render.)
    setTranslated(text);

    _enqueue(lang, text).then((out) => {
      if (tokenRef.current === myToken) setTranslated(out);
    });
  }, [text, lang]);

  return translated;
}

// Named export — returns { translated, isPending } so a caller can
// pulse the UI while the batch resolves. Optional; existing
// callsites don't need to change.
export function useCaptionTranslationWithPending(text, lang) {
  const [translated, setTranslated] = useState(() => _initialValue(text, lang));
  const [isPending, setIsPending] = useState(false);
  const tokenRef = useRef(0);

  useEffect(() => {
    if (!text) { setTranslated(""); setIsPending(false); return; }
    if (!lang || lang === "en") { setTranslated(text); setIsPending(false); return; }
    const key = `${lang}::${text}`;
    if (cache.has(key)) { setTranslated(cache.get(key)); setIsPending(false); return; }
    const myToken = ++tokenRef.current;
    setTranslated(text);
    setIsPending(true);
    _enqueue(lang, text).then((out) => {
      if (tokenRef.current === myToken) {
        setTranslated(out);
        setIsPending(false);
      }
    });
  }, [text, lang]);

  return { translated, isPending };
}
